import { DatePipe } from "@angular/common";
import {
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  ViewChild,
  effect,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Router } from "@angular/router";
import { QuestionnaireStore } from "src/app/features/store/questionnaire-store";
import { Button } from "src/app/shared/components/utils/button/button";
import { Icon } from "src/app/shared/components/utils/icon/icon";
import { Modal } from "src/app/shared/components/utils/modal/modal";
import { Loader } from "src/app/shared/components/loader/loader";
import { PianoAnalyticsService } from "src/app/core/services/piano-analytics";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { LanguageService } from "src/app/core/services/language-service";
import { QuestionnairePdfService } from "src/app/features/services/questionnaire/questionnaire-pdf-service";
import { QuestionnaireSection } from "src/app/shared/components/questionnaire-section/questionnaire-section";

@Component({
  selector: "app-portal-questionnaire-page",
  imports: [
    Button,
    Icon,
    DatePipe,
    Modal,
    Loader,
    TranslocoModule,
    QuestionnaireSection,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./portal-questionnaire-page.html",
})
export class PortalQuestionnairePage {
  piano = inject(PianoAnalyticsService);
  readonly router = inject(Router);
  readonly questionnaireStore = inject(QuestionnaireStore);
  private readonly translocoService = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly questionnairePdf = inject(QuestionnairePdfService);

  isLoading = signal<boolean>(false);
  isPrinting = signal(false);

  @ViewChild("printContainer") printContainer?: ElementRef<HTMLElement>;

  constructor() {
    effect(() => {
      document.body.style.overflow = this.isModalOpen() ? "hidden" : "";
    });

    effect(async () => {
      this.languageService.currentLanguage();
      this.isLoading.set(true);
      await this.questionnaireStore.loadQuestionnaire();
      this.isLoading.set(false);
    });
  }

  isInitializing = signal(false);
  isDeleteModalOpen = signal<boolean>(false);
  isRestartModalOpen = signal<boolean>(false);
  isModalOpen = computed(
    () => this.isDeleteModalOpen() || this.isRestartModalOpen()
  );

  hasQuestionnaire = computed(() => this.questionnaireStore.hasQuestionnaire());
  isQuestionnaireLoaded = computed(() =>
    this.questionnaireStore.isQuestionnaireLoaded()
  );
  questionnaireScore = computed(() =>
    this.questionnaireStore.questionnaireScore()
  );
  maturityLevel = computed(() => this.questionnaireStore.maturityLevel());
  isNewMode = computed(() => this.questionnaireStore.isNewMode());

  questionnaireState = computed(() =>
    this.questionnaireStore.getQuestionnaireState()
  );

  startButtonTextKey = computed(() => {
    if (!this.hasQuestionnaire() || this.isNewMode()) {
      return "portalQuestionnaire.startCtaText";
    }
    return "portalQuestionnaire.restartCtaText";
  });

  get currentLocale(): string {
    return this.translocoService.getActiveLang();
  }

  startOrRestartQuestionnaire(): void {
    if (!this.hasQuestionnaire() || this.isNewMode()) {
      this.startQuestionnaire();
    } else {
      this.openRestartModal();
    }
  }

  async startQuestionnaire(): Promise<void> {
    try {
      this.isInitializing.set(true);
      this.isLoading.set(true);
      await this.questionnaireStore.createQuestionnaire();
      await this.questionnaireStore.loadQuestionnaire();
      this.piano.sendEvent("questionnaire.opening", {});
      this.router.navigate([
        "/user-profile/portal-questionnaire/questionnaire",
      ]);
    } catch (error) {
      console.error("Erreur lors du démarrage du questionnaire:", error);
    } finally {
      this.isInitializing.set(false);
      this.isLoading.set(false);
      this.closeRestartModal();
    }
  }

  async continueQuestionnaire(): Promise<void> {
    this.router.navigate(["/user-profile/portal-questionnaire/questionnaire"]);
    this.piano.sendEvent("questionnaire.opening", {});
  }

  async deleteQuestionnaire(): Promise<void> {
    try {
      await this.questionnaireStore.deleteQuestionnaire();
    } finally {
      this.closeDeleteModal();
    }
  }

  openDeleteModal(): void {
    this.isDeleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen.set(false);
  }

  openRestartModal(): void {
    this.isRestartModalOpen.set(true);
  }

  closeRestartModal(): void {
    this.isRestartModalOpen.set(false);
    document.body.style.overflow = "";
  }

  async downloadQuestionnaire(): Promise<void> {
    if (!this.hasQuestionnaire()) return;

    this.isPrinting.set(true);

    const container = await this.waitForPrintContainer();
    if (container) {
      await this.questionnairePdf.download(container);
    }

    this.isPrinting.set(false);
  }

  private waitForPrintContainer(): Promise<HTMLElement | null> {
    return new Promise((resolve) => {
      const check = (): void => {
        const el = this.printContainer?.nativeElement;
        if (el?.querySelector("questionnaire-section")) {
          requestAnimationFrame(() => resolve(el));
        } else {
          requestAnimationFrame(check);
        }
      };
      requestAnimationFrame(check);
    });
  }
}
